import { X, Copy, Download, AlertTriangle, CheckCircle, Brain } from 'lucide-react';
import { useState } from 'react';
import type { ThreatFeedItem } from '../lib/api';
import { AttributionBadge } from './AttributionBadge';
import { SgisBadge } from './SgisBadge';
import { DetectionMethodsGroup } from './DetectionMethodBadge';
import { safeDate } from '../lib/validation';
import { getTechniqueName, getTechniqueDescription, getTechniqueCategory, getTechniqueCategoryColor } from '../lib/mitre-techniques';
import { ENGAGEMENT_VOCAB } from '../lib/engagement-vocab';

interface ThreatDetailPanelProps {
  threat: ThreatFeedItem;
  onClose: () => void;
}

export function ThreatDetailPanel({ threat, onClose }: ThreatDetailPanelProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportAsJSON = () => {
    const dataStr = JSON.stringify(threat, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `threat-${threat.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full md:w-[600px] bg-sg-dark border-l border-sg-border z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-sg-dark/95 backdrop-blur border-b border-sg-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Threat Details</h2>
            <p className="text-sm text-gray-400 mt-1">Session: {threat.sessionId.slice(0, 16)}...</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-all"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => copyToClipboard(threat.sourceIp)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-sg-card border border-sg-border rounded-lg hover:border-blue-500/50 transition-all text-sm"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy IP'}
            </button>
            <button
              onClick={exportAsJSON}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-sg-card border border-sg-border rounded-lg hover:border-blue-500/50 transition-all text-sm"
            >
              <Download className="w-4 h-4" />
              Export JSON
            </button>
          </div>

          {/* Threat Level */}
          <div className="bg-sg-card border border-sg-border rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-3 font-medium">THREAT ASSESSMENT</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-400 mb-1">Level</div>
                <div className={`inline-block px-3 py-1 rounded text-sm font-semibold ${
                  threat.level === 'critical' ? 'bg-red-500/20 text-red-400' :
                  threat.level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                  threat.level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {threat.level.toUpperCase()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400 mb-1">Confidence</div>
                <div className="text-2xl font-bold text-white">{threat.confidence}%</div>
              </div>
            </div>
          </div>

          {/* SGIS Intelligence */}
          {threat.sgisScore !== undefined && (
            <div className="bg-sg-card border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-4 h-4 text-blue-400" />
                <div className="text-xs text-blue-400 font-medium">SGIS INTELLIGENCE</div>
              </div>
              
              <div className="space-y-4">
                {/* SGIS Score */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Intelligence Score</div>
                    <div className="text-sm text-gray-300">Composite threat rating</div>
                  </div>
                  <SgisBadge score={threat.sgisScore} size="lg" />
                </div>

                {/* Detection Methods */}
                <div>
                  <div className="text-xs text-gray-400 mb-2">Detection Methods</div>
                  <DetectionMethodsGroup 
                    honeypot={threat.honeypotTriggered}
                    datacenter={threat.datacenterTraffic}
                    headerAnomalies={threat.headerAnomalies}
                    requestsPerMinute={threat.requestsPerMinute}
                  />
                </div>

                {/* Request Velocity */}
                {threat.requestsPerMinute !== undefined && threat.requestsPerMinute > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                    <span className="text-sm text-gray-400">Request Velocity</span>
                    <span className={`text-white font-semibold ${
                      threat.requestsPerMinute > 60 ? 'text-red-400' :
                      threat.requestsPerMinute > 30 ? 'text-orange-400' :
                      'text-gray-300'
                    }`}>
                      {threat.requestsPerMinute} req/min
                    </span>
                  </div>
                )}

                {/* Info Text */}
                <div className="text-xs text-gray-500 pt-2 border-t border-gray-700">
                  SGIS (ShadowGuard Intelligence Score) uses honeypot detection, ASN intelligence, 
                  header fingerprinting, and velocity analysis to assess threat level.
                </div>
              </div>
            </div>
          )}

          {/* Source Information */}
          <div className="bg-sg-card border border-sg-border rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-3 font-medium">SOURCE</div>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">IP Address</div>
                <div className="font-mono text-white">{threat.sourceIp}</div>
              </div>
              {threat.sourceCountry && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="text-white">
                    {threat.sourceCity && `${threat.sourceCity}, `}{threat.sourceCountry}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-gray-500 mb-2">Attribution</div>
                <div className="flex flex-wrap gap-2">
                  {threat.isTor && <AttributionBadge type="tor" label="TOR" tooltip="Tor Exit Node" />}
                  {threat.isCloud && threat.cloudProvider && (
                    <AttributionBadge type="cloud" label={threat.cloudProvider} tooltip={`Cloud: ${threat.cloudProvider}`} />
                  )}
                  {threat.isKnownScanner && threat.scannerName && (
                    <AttributionBadge type="scanner" label={threat.scannerName} tooltip={`Scanner: ${threat.scannerName}`} />
                  )}
                  {!threat.isTor && !threat.isCloud && !threat.isKnownScanner && (
                    <span className="text-sm text-gray-500">No special attribution</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Attack Techniques */}
          <div className="bg-sg-card border border-sg-border rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-3 font-medium">ATTACK TECHNIQUES ({threat.techniques.length})</div>
            <div className="space-y-3">
              {threat.techniques.map((techId) => {
                const category = getTechniqueCategory(techId);
                const colorClass = getTechniqueCategoryColor(category);
                return (
                  <div key={techId} className={`p-3 rounded-lg border ${colorClass}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-medium text-sm">{getTechniqueName(techId)}</div>
                      <code className="text-xs font-mono opacity-60 whitespace-nowrap">{techId}</code>
                    </div>
                    <div className="text-xs opacity-75">{getTechniqueDescription(techId)}</div>
                    <div className="mt-2 text-xs text-gray-500">Category: {category}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Activity */}
          <div className="bg-sg-card border border-sg-border rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-3 font-medium">ACTIVITY</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Request Count</span>
                <span className="text-white font-semibold">{threat.requestCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">First Seen</span>
                <span className="text-white text-sm">{safeDate(threat.timestamp)}</span>
              </div>
              {threat.ja3Hash && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">JA3 Fingerprint</div>
                  <div className="font-mono text-xs text-white break-all">{threat.ja3Hash}</div>
                </div>
              )}
            </div>
          </div>

          {/* Deception status — session-level flag means decoy delay / interaction occurred */}
          {threat.deceptionEngaged && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <div className="text-sm font-semibold text-green-400">{ENGAGEMENT_VOCAB.decoyTimeBurned.label}</div>
              </div>
              <p className="text-sm text-gray-300">
                {ENGAGEMENT_VOCAB.decoyTimeBurned.hint} That does not always mean a deep breadcrumb callback
                ({ENGAGEMENT_VOCAB.deepCallbacks.short.toLowerCase()}).
              </p>
            </div>
          )}

          {/* Recommended Actions */}
          <div className="bg-sg-card border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <div className="text-xs text-orange-400 font-medium">RECOMMENDED ACTIONS</div>
            </div>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-orange-400 mt-1">•</span>
                <span>Review attack patterns and update firewall rules to block similar traffic</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400 mt-1">•</span>
                <span>Monitor for additional activity from this IP or related infrastructure</span>
              </li>
              {threat.isTor && (
                <li className="flex items-start gap-2">
                  <span className="text-orange-400 mt-1">•</span>
                  <span>Consider implementing Tor exit node blocking if appropriate for your use case</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="text-orange-400 mt-1">•</span>
                <span>Export to STIX format for sharing with security team or SIEM integration</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
